import { useReducedMotion } from 'motion/react';
import {
    createContext,
    type ReactNode,
    useContext,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

const CardTransitionContext = createContext({ height: false, fade: false });

/** Keeps text at its natural size while a live card contracts into a receipt.
 * Initial renders stay still, including historical rows remounted by the
 * virtualizer. Observing the content also covers wrapping and disclosures. */
export function CardTransition(
    props: Readonly<{
        readonly children: ReactNode;
        readonly transitionKey: string;
    }>,
): ReactNode {
    return <TransitionFrame {...props} />;
}

/** Resizes a container such as the composer or approval stack without
 * owning the state change of any card inside it. */
export function CardResize({
    children,
}: Readonly<{ readonly children: ReactNode }>): ReactNode {
    return <TransitionFrame>{children}</TransitionFrame>;
}

function TransitionFrame({
    children,
    transitionKey,
}: Readonly<{
    readonly children: ReactNode;
    readonly transitionKey?: string;
}>): ReactNode {
    const contentRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const previousContent = useRef<HTMLElement | null>(null);
    const previousKey = useRef(transitionKey);
    const [heightPx, setHeightPx] = useState<number | null>(null);
    const reduceMotion = useReducedMotion();
    const parent = useContext(CardTransitionContext);

    useLayoutEffect(() => {
        const content = contentRef.current;
        if (
            parent.height ||
            content === null ||
            typeof ResizeObserver === 'undefined'
        ) {
            return;
        }
        const measure = () =>
            setHeightPx(content.getBoundingClientRect().height);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(content);
        return () => observer.disconnect();
    }, [parent.height]);

    useLayoutEffect(() => {
        const changed = previousKey.current !== transitionKey;
        previousKey.current = transitionKey;
        const content = contentRef.current;
        if (
            !changed ||
            parent.fade ||
            reduceMotion ||
            content?.animate === undefined
        ) {
            return;
        }
        // Paint the outgoing state briefly, but never retain a second live
        // form. Its snapshot cannot receive focus, announce or submit.
        const outgoing = previousContent.current;
        if (outgoing !== null) {
            outgoing.setAttribute('inert', '');
            outgoing.setAttribute('aria-hidden', 'true');
            outgoing
                .querySelectorAll('[id]')
                .forEach((node) => node.removeAttribute('id'));
            Object.assign(outgoing.style, {
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                pointerEvents: 'none',
            });
            frameRef.current?.append(outgoing);
        }
        const fadeOut = outgoing?.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: 100,
            fill: 'forwards',
        });
        const fadeIn = content.animate(
            [{ opacity: 0 }, { opacity: 0, offset: 0.2 }, { opacity: 1 }],
            { duration: 300, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        );
        if (fadeOut !== undefined) {
            fadeOut.onfinish = () => outgoing?.remove();
        }
        return () => {
            fadeIn.cancel();
            fadeOut?.cancel();
            outgoing?.remove();
        };
    }, [transitionKey, reduceMotion, parent.fade]);

    useLayoutEffect(() => {
        // Refresh after edits too, so the outgoing paint matches what the
        // coordinator last saw rather than the original proposed message.
        const clone =
            transitionKey === undefined || parent.fade
                ? null
                : contentRef.current?.cloneNode(true);
        previousContent.current = clone instanceof HTMLElement ? clone : null;
    });

    return (
        <div
            ref={frameRef}
            data-card-transition={true}
            className="relative min-w-0 transition-[height] duration-[320ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
            style={heightPx === null ? undefined : { height: heightPx }}
        >
            <CardTransitionContext.Provider
                value={{
                    height: true,
                    fade: parent.fade || transitionKey !== undefined,
                }}
            >
                <div ref={contentRef} className="flow-root min-w-0">
                    {children}
                </div>
            </CardTransitionContext.Provider>
        </div>
    );
}
